import { RJSFSchema } from '@rjsf/utils';
import { Audience } from './audiences';

export type DistributionChannelAudience = {
  id: string;
  name: string;
  createdOn: string; // date-time
  scheduleType: Audience['scheduleType'];
};

export type DistributionChannelTrait = {
  id: string;
  displayName: string;
  dotName: string;
  identifier: boolean;
  dataType: string;
  type: string;
};

export type DistributionChannelAudienceTrait = {
  id: string;
  displayName: string;
  dotName: string;
  identifier: boolean;
  dataType: string;
  type: string;
};

export type DistributionChannel = {
  id: string;
  audienceId: string;
  audience: DistributionChannelAudience;
  name: string;
  destination: string;
  active: boolean;
  integrationType: string;
  integrationPartner: string;
  allowAddUsers: boolean;
  allowUpdateUsers: boolean;
  allowRemoveUsers: boolean;
  trait: DistributionChannelTrait;
  audienceTrait: DistributionChannelAudienceTrait;
  createdOn: string; // date-time
  status: string;
  configuration: any;
  mergeStrategy: string;
};

export type CycleConnector = {
  Id: string;
  Name: string;
  Version: string;
  Icon: string;
};

export type CycleAccountConnector = {
  Id: number;
  Name: string;
  Authenticated: boolean;
};

export type CycleStep = {
  Id: string;
  Name: string;
  ActionType: string;
  AccountConnector: CycleAccountConnector;
};

export type CycleParameterTrigger = {
  AccountConnectorId: number;
  EntityId: number;
  TriggerType: string;
  TriggerId: number;
  MethodId: number;
  Name: string;
  HumanReadableKey: string;
  IdentifierKey: string;
};

export type CycleParameter = {
  Parameter: {
    TargetType: string;
    TargetName: string;
    IsSensitive: boolean;
    Id: number;
    Name: string;
    Description: string;
    IsOptional: boolean;
    DataType: string;
    TriggerName: string;
    Values: string[];
    DisplayOrder: number;
    Triggers: CycleParameterTrigger[];
  };
  MappingType: string;
  SourceFieldId: number;
  SourceStepId: string;
  TriggerValue: string;
  TriggerValueDisplayName: string;
  Value: string;
};

export type CyclePrerequisite = {
  Step: CycleStep;
  Parameters: CycleParameter[];
};

export type Cycle = {
  Id: string;
  Name: string;
  Description: string;
  CreatedOnUtc: string;
  Connectors: CycleConnector[];
  Status: string;
  TemplateId: string;
  TemplateReleaseId: string;
  Prerequisites: CyclePrerequisite[];
};

export type CyclrActionParameterLookupValue = {
  Name: string;
  Value: string;
};

export type CyclrActionTrigger = {
  AccountConnectorId: number;
  EntityId: number;
  TriggerType: string | null;
  TriggerId: number;
  MethodId: number;
  Name: string;
  HumanReadableKey: string;
  IdentifierKey: string;
};

export type CyclrActionTriggerValue = {
  name: string;
  value: string;
  nextTrigger: CyclrActionTriggerParameterLookupValue | null;
};

export type CyclrActionTriggerParameterLookupValue = {
  trigger: CyclrActionTrigger;
  values: CyclrActionTriggerValue[];
};

export type CyclrActionParameter = {
  isAccountConnectorParameter: boolean;
  name: string;
  description: string;
  isOptional: boolean;
  dataType: string;
  displayOrder: number;
  stepIds: string[];
  parameterId: number;
  mappingType: string;
  value: any;
  lookupValues: CyclrActionParameterLookupValue[];
  triggers: CyclrActionTrigger[];
  triggerParameterLookupValues: CyclrActionTriggerParameterLookupValue;
  accountConnectorId: number;
  propertyId: number;
};

export type CyclrActionField = {
  description: string;
  isOptional: boolean;
  name: string;
  dataType: string;
  stepId: string;
  fieldId: number;
  mappingType: string;
  value: any;
};

export type CyclrAction = {
  status: string;
  parameters: CyclrActionParameter[];
  fields: CyclrActionField[];
  token: string;
  authUrl: string;
  accountConnectors: number[];
};

export type Cyclr = {
  cycle: Cycle;
  status: string;
  action: CyclrAction;
};

export type DistributionChannelIntegrationResult = {
  cyclr: Cyclr;
};

export type DistributionChannelContent = {
  distributionChannel: DistributionChannel;
  integrationsResult: DistributionChannelIntegrationResult;
};

export type DistributionChannelParameterUpdate = {
  isAccountConnectorParameter: boolean;
  accountConnectorId: number;
  propertyId: number;
  stepIds: string[];
  parameterId: number;
  mappingType: string;
  value: string;
  name: string | null;
};

export type DistributionChannelAdvancedParameter = {
  allowAddUsers: boolean;
  allowUpdateUsers: boolean;
  allowRemoveUsers: boolean;
  activate: boolean;
};

export type DistributionChannelFieldUpdate = {
  stepId: string;
  fieldId: number;
  mappingType: string;
  value: string;
};

export type DistributionChannelLastCyclrTransaction = {
  HasErrors: boolean;
  LastRunTimestamp: string;
};

export enum IntegrationPartner {
  CYCLR = 'CYCLR',
  SEI = 'SEI',
}

export type DistributionChannelConnector = {
  name: string;
  description: string;
  installed: boolean;
  authenticated: boolean;
  integrationPartner: IntegrationPartner;
  accountConnectorId?: number;
  icon: string;
};

export enum DISTRIBUTION_CHANNEL_STATUS {
  CREATE = 'CREATE',
  PARAMETERS_REQUIRED = 'PARAMETERS_REQUIRED',
  AUTHENTICATION_REQUIRED = 'AUTHENTICATION_REQUIRED',
  ACTIVABLE = 'ACTIVABLE',
}

// SEI
type TraitsHandler = {
  javaClass: string;
};

type DistributionElement = {
  traitsMappingRequired: string[];
  traitsMappingOptional: string[];
  traitsHandler: TraitsHandler[];
};

type ReactJsonSchemaForm = {
  formId: string;
  title: string;
  schema: RJSFSchema;
};

type ConfigurationElement = {
  reactJsonSchemaForm: ReactJsonSchemaForm;
};

type AuthElement = ConfigurationElement & {
  type: AuthType;
};

enum AuthType {
  USER_PASS = 'USER_PASS',
}

type ConfigurationVersion = {
  version: string;
  date: string;
  elements: {
    auth: AuthElement;
    configuration: ConfigurationElement;
    distribution: DistributionElement;
  };
};

export type CustomConnectorConfiguration = {
  configurations: ConfigurationVersion[];
  currentVersion: string;
  lastUpdate: string;
};
